import { Badge } from "@/components/ui/badge";
import { IntegrationType } from "../../types";

interface TypeSelectorProps {
  integrationTypes: IntegrationType[];
  onSelectType: (type: "api" | "mcp" | "text_document") => void;
}

const getIntegrationDescription = (id: string): string => {
  switch (id) {
    case "text_document":
      return "URL to text-based content (markdown, HTML, JSON, YAML, etc). System intelligently analyzes and discovers related documents.";
    case "api":
      return "REST API endpoint. Works best with OpenAPI specs or capability registries, but system can auto-discover endpoints via sitemap or documentation.";
    case "mcp":
      return "Model Context Protocol server for structured data exchange.";
    default:
      return "";
  }
};

const TypeSelector = ({ integrationTypes, onSelectType }: TypeSelectorProps) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
        <p className="text-xs sm:text-sm text-blue-900">
          <span className="font-semibold">How it works:</span> Help Genie and its agents do not store context data. Context is fetched from your source in real-time on demand.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
        <p className="text-xs sm:text-sm text-amber-900">
          <span className="font-semibold">Note:</span> The knowledge base feature is more effective and reliable for providing agent context. Use this integration approach if data ownership and real-time fetching are a priority for your use case.
        </p>
      </div>

      <div className="space-y-3">
        {integrationTypes.map((integration) => (
          <div key={integration.id} className="relative">
            <button
              onClick={() =>
                integration.enabled && onSelectType(integration.id as "text_document" | "api" | "mcp")
              }
              disabled={!integration.enabled}
              className={`w-full text-left p-4 sm:p-5 border-2 rounded-lg transition-all ${
                integration.enabled
                  ? "border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-sm cursor-pointer"
                  : "border-gray-200 bg-gray-50 cursor-not-allowed"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                    {integration.label}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1.5 leading-relaxed">
                    {getIntegrationDescription(integration.id)}
                  </p>
                </div>
                {!integration.enabled && (
                  <Badge className="flex-shrink-0 bg-[#FFBB33]/20 text-[#FFBB33] text-xs font-medium whitespace-nowrap ml-2">
                    Coming soon
                  </Badge>
                )}
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TypeSelector;
